import { useEffect, useRef, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, StarOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { Rate } from "antd";
import { updateDataSetRequest } from "store/dataSets/actions";
import { fetchDataSetById } from "./dataSetApi";
import { buildToolError } from "./errorEnvelope";
import type { ToolPartProps } from "./types";

interface UpdateDataSetRatingInput {
    rating: number;
    description: string;
}

interface UpdateDataSetRatingOutput {
    success: boolean;
    cancelled?: boolean;
    error?: string;
    rating?: number;
    verified?: boolean;
    appliedChanges?: string[];
    unappliedChanges?: string[];
}

type Phase = "ready" | "denied" | "applying" | "applied" | "error";

// Clamp to the backend-accepted range (0-5, whole numbers). See
// dataset-service updateDataSetSchema / DATA_SET_HEADER_METADATA_RATING.
function normaliseRating(value: unknown): number | null {
    const n = Math.round(Number(value));
    if (!Number.isFinite(n) || n < 0 || n > 5) return null;
    return n;
}

export default function UpdateDataSetRatingTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<UpdateDataSetRatingInput, UpdateDataSetRatingOutput>) {
    const { dispatch, addToolOutput, businessAreaId, schemaId, tenantId, dataSet, mode, permission, updateDataSetResult } = helpers;

    const [phase, setPhase] = useState<Phase>("ready");
    const [denyReason, setDenyReason] = useState<string | null>(null);
    const initialisedRef = useRef(false);
    const resolvedRef = useRef(false);
    // Snapshot of updateDataSetResult at dispatch; ignore stale prior results.
    const resultAtDispatchRef = useRef<unknown>(null);

    const proposedRating = normaliseRating(input?.rating);
    const currentRating = dataSet?.metadata?.rating ?? 0;
    const noChange = proposedRating !== null && proposedRating === currentRating;

    useEffect(() => {
        if (initialisedRef.current) return;
        if (state !== "input-available") return;
        initialisedRef.current = true;

        if (mode === "view" || !permission?.canEdit) {
            setPhase("denied");
            setDenyReason("You're in read-only mode for this data set, so I can't change its rating.");
            return;
        }
        if (!dataSet || !dataSet.dataSetId) {
            setPhase("denied");
            setDenyReason("No data set is loaded.");
            return;
        }
        if (proposedRating === null) {
            setPhase("denied");
            setDenyReason("The rating must be a whole number from 0 to 5.");
            return;
        }
    }, [state, mode, permission, dataSet, proposedRating]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state === "output-available" || state === "output-error") return;
        if (phase === "denied") {
            resolvedRef.current = true;
            addToolOutput({ tool: "updateDataSetRating", toolCallId, output: { success: false, error: denyReason || "Cannot update rating." } });
        }
    }, [phase, denyReason, state, toolCallId, addToolOutput]);

    useEffect(() => {
        if (phase !== "applying") return;
        if (!updateDataSetResult) return;
        if (updateDataSetResult === resultAtDispatchRef.current) return;
        if (updateDataSetResult.success === true) {
            (async () => {
                let refreshed: { metadata?: { rating?: number } } | null = null;
                let fetchErr: string | null = null;
                try {
                    if (tenantId && businessAreaId && schemaId && dataSet?.dataSetId) {
                        refreshed = (await fetchDataSetById(tenantId, businessAreaId, schemaId, dataSet.dataSetId)) as any;
                    }
                } catch (e: any) {
                    fetchErr = e?.message || "fetch error";
                }
                const applied: string[] = [];
                const unapplied: string[] = [];
                if (fetchErr) {
                    // The PUT already returned success; the verification GET is
                    // best-effort. Trust the successful write rather than raising
                    // a false "not applied" alarm.
                    applied.push(`rating set to ${proposedRating}`);
                } else {
                    const serverRating = refreshed?.metadata?.rating ?? 0;
                    if (serverRating === proposedRating) applied.push(`rating set to ${proposedRating}`);
                    else unapplied.push(`rating change to ${proposedRating} did not apply (server has ${serverRating})`);
                }
                setPhase("applied");
                if (!resolvedRef.current) {
                    resolvedRef.current = true;
                    addToolOutput({
                        tool: "updateDataSetRating",
                        toolCallId,
                        output: {
                            success: true,
                            rating: proposedRating ?? undefined,
                            verified: !fetchErr && unapplied.length === 0,
                            appliedChanges: applied,
                            unappliedChanges: unapplied,
                        },
                    });
                }
            })();
        } else if (updateDataSetResult.success === false) {
            setPhase("error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "updateDataSetRating",
                    toolCallId,
                    output: buildToolError(updateDataSetResult, "Rating update failed."),
                });
            }
        }
    }, [updateDataSetResult, phase, proposedRating, dataSet, toolCallId, addToolOutput, tenantId, businessAreaId, schemaId]);

    const handleApply = () => {
        if (!businessAreaId || !schemaId || !dataSet || proposedRating === null) return;
        // The backend REPLACES metadata wholesale (dataSetMapper.toDataSetUpdateItem),
        // so we must resend the existing tags alongside the new rating or they would
        // be wiped. Preserve whatever tags the dataset currently carries.
        const updatedDataSet: Record<string, unknown> = {
            metadata: {
                rating: proposedRating,
                tags: dataSet.metadata?.tags ?? {},
            },
        };
        resultAtDispatchRef.current = updateDataSetResult;
        setPhase("applying");
        // source="assistant" so the DataSets container does not run its legacy
        // post-save "navigate back to Schemas" redirect (PRE-2623 bug 4).
        dispatch(updateDataSetRequest(businessAreaId, schemaId, dataSet, updatedDataSet, "assistant"));
    };

    const handleCancel = () => {
        if (!resolvedRef.current) {
            resolvedRef.current = true;
            addToolOutput({ tool: "updateDataSetRating", toolCallId, output: { success: false, cancelled: true } });
        }
    };

    return (
        <div className="rounded-2xl border border-[#001D3D]/40 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-[#001D3D]">
                <StarOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Update quality rating</span>
            </div>
            <div className="text-base font-semibold text-zinc-900">{dataSet?.dataSetName || "(no data set)"}</div>
            {input?.description && <div className="mt-1 text-xs text-zinc-600">{input.description}</div>}

            {phase === "denied" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {denyReason}
                </div>
            )}

            {phase !== "denied" && proposedRating !== null && (
                <div className="mt-2 flex items-center gap-3 text-xs">
                    <span className="text-zinc-500">New rating:</span>
                    <Rate value={proposedRating} disabled />
                    <span className="font-semibold text-zinc-900">{proposedRating}/5</span>
                    {noChange && <span className="text-zinc-500">(already set to this)</span>}
                </div>
            )}

            {phase === "applied" && output?.verified !== false && (output?.unappliedChanges?.length || 0) === 0 && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Rating updated.
                </div>
            )}
            {phase === "applied" && (output?.verified === false || (output?.unappliedChanges?.length || 0) > 0) && (
                <div className="mt-3 rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1 text-xs text-yellow-800">
                    <div className="flex items-center gap-2 font-medium">
                        <WarningOutlined /> The rating change did not apply
                    </div>
                    <ul className="ml-4 mt-1 list-disc">
                        {(output?.unappliedChanges || []).map((c, i) => (
                            <li key={i}>{c}</li>
                        ))}
                    </ul>
                </div>
            )}
            {phase === "error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> Rating update failed.
                </div>
            )}
            {state === "output-available" && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {phase === "ready" && state !== "output-available" && state !== "output-error" && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button type="button" onClick={handleCancel} className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        className="rounded-md bg-[#001D3D] px-3 py-1 text-xs font-medium text-white hover:bg-[#002D5C]"
                    >
                        Apply
                    </button>
                </div>
            )}
            {phase === "applying" && (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Applying...
                </div>
            )}
        </div>
    );
}
